'use client'

import * as React from 'react'
import { cn } from '@segway/core'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select' // Assuming your select component is in this path

export interface FloatingLabelSelectProps extends React.ComponentPropsWithoutRef<typeof Select> {
  label?: string
  required?: boolean
  floatingLabel?: boolean
  error?: boolean
  options: { value: string; label: string }[]
  triggerClassName?: string
  labelStyle?: string
}

const FloatingLabelSelect = React.forwardRef<HTMLButtonElement, FloatingLabelSelectProps>(
  (
    {
      label = '',
      required = false,
      floatingLabel = true,
      error = false,
      options,
      disabled = false,
      triggerClassName,
      labelStyle = '',
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false)
    const [hasValue, setHasValue] = React.useState(false)

    // Check if value exists
    React.useEffect(() => {
      setHasValue(!!props.value)
    }, [props.value])

    const shouldFloat = React.useMemo(() => {
      return label && floatingLabel && (open || hasValue)
    }, [label, floatingLabel, open, hasValue])

    return (
      <div className="relative">
        <Select
          {...props}
          open={open}
          onOpenChange={(isOpen) => {
            setOpen(isOpen)
            if (props.onOpenChange) {
              props.onOpenChange(isOpen)
            }
          }}
          onValueChange={(value) => {
            setHasValue(!!value)
            if (props.onValueChange) {
              props.onValueChange(value)
            }
          }}>
          <div className="relative">
            <SelectTrigger
              ref={ref}
              disabled={disabled}
              iconColor="#000"
              className={cn(
                'flex w-full items-center justify-between rounded-md border bg-white text-base focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                shouldFloat ? 'pb-2 pt-6' : 'py-2',
                error
                  ? 'border-primary focus-visible:border-primary'
                  : 'border-gray-2 focus-visible:border-black',
                triggerClassName,
              )}>
              <div className="line-clamp-1 flex-1 text-left">
                <SelectValue placeholder="" />
              </div>
            </SelectTrigger>
            {label && (
              <label
                className={cn(
                  'pointer-events-none absolute left-4 transition-all duration-200',
                  shouldFloat ? 'top-1.5 text-sm' : 'top-1/2 -translate-y-1/2 text-base',
                  !floatingLabel && hasValue ? 'opacity-0' : '',
                  disabled ? 'text-gray-5' : error ? 'text-gray' : 'text-gray',
                  labelStyle,
                )}>
                {label}
                {required && (
                  <span className={cn('ml-0.5', disabled ? 'text-gray-5' : 'text-primary')}>*</span>
                )}
              </label>
            )}
          </div>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value} className="line-clamp-1">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  },
)

FloatingLabelSelect.displayName = 'FloatingLabelSelect'

export default FloatingLabelSelect
